import { Box, Divider, FormControlLabel, MenuItem, Tooltip, Typography, useTheme } from "@mui/material";
import { AppMenu } from "../../../components/common/AppMenu";
import React, { memo, ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { ExtraSmallButton } from "../../../components/custom";
import MultiPaneMenuIcon from "../../../assets/icons/MultiPaneMenuIcon";
import { selectMultiPaneFullScreen } from "../../../selectors/common";
import { useSelector } from "react-redux";
import { MULTIPANE_FULLSCREEN_MODE_ID, SCALPER_LAYOUT_ID } from "../../../assets/constants";
import { ScalperLayoutName, ScalperLayoutToPredefinedLayoutsMap } from "../../../util/scalper";
import { ScalperLayouts } from "../scalper.typings";
import { PredefinedDashboard } from "../../../components/widgetDashboard/widgetDashboard.enums";
import { useDispatch } from "react-redux";
import { setScalperLayout } from "../../../reducer/scalper.reducer";
import { DashboardSyncSettings, PredefinedDashboardMenuType } from "../../../components/widgetDashboard/widgetDashboard.interface";
import { selectSyncSettings } from "../../../selectors/widgetDashboard";
import { updateSyncSettings } from "../../../reducer/widgetDashboard.reducer";
import ToggleSwitch from "../../../components/common/toggleSwitch/toggleSwitch";
import { syncSettingsMenuItems } from "../../../components/multiPaneMenu/multiPaneMenu";
import { ShortcutTooltipTitle, ShortcutKeys } from "../../../components/KeyboardShortcuts/ShortcutTooltipTitle";

const ScalperLayoutMenu: React.FC = () => {

    const dispatch = useDispatch();
    const buttonRef = useRef(null)
    const [anchorEl, setAnchorEl] = useState(null);
    const container = useSelector(selectMultiPaneFullScreen)
    const syncSettings = useSelector(selectSyncSettings(SCALPER_LAYOUT_ID));

    const syncSettingsKeys: Array<keyof DashboardSyncSettings> = ["crosshair", "timeBucket", "time"]
    const theme = useTheme();
    const handleOpenPopover = useCallback((event: any) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const open = useMemo(() => Boolean(anchorEl), [anchorEl])

    const handleDashboardClick = useCallback((scalperLayout: ScalperLayouts, dashboard: PredefinedDashboard) => {
        dispatch(setScalperLayout({ scalperLayout, predefinedLayout: dashboard, force: true }))
    }, [])

    const menuItems = useMemo(() => {
        const items: ReactElement[] = [];
        for (let key in ScalperLayoutToPredefinedLayoutsMap) {
            if (key === ScalperLayouts.CALL_AND_PUT) {
                // debugger;
            }
            items.push(
                <React.Fragment key={key}>
                    <Typography variant={'caption'} color="textSecondary" sx={{ p: theme.spacing(1, 2), textTransform: "uppercase" }}>{ScalperLayoutName[key]}</Typography>
                    <Box display={'flex'} flexDirection="row" justifyContent="flex-start" sx={{px: '12px'}} >
                    {ScalperLayoutToPredefinedLayoutsMap[key].map((dashboard: PredefinedDashboardMenuType) => (
                        <ExtraSmallButton
                            key={dashboard.id}
                            color={'inherit'}
                            onClick={() => handleDashboardClick(key as ScalperLayouts, dashboard.id)}
                            sx={{
                                padding: theme.spacing(0,0.5)
                            }}
                        >
                            {dashboard.icon}
                        </ExtraSmallButton>))}
                    </Box>
                </React.Fragment>
            )
        }

        return items;
    }, [handleDashboardClick, theme])

    const handleSwitchChange = useCallback((key: keyof DashboardSyncSettings) => {
        dispatch(updateSyncSettings({
            id: SCALPER_LAYOUT_ID,
            syncSettings: {
                ...syncSettings,
                [key]: !syncSettings[key]
            }
        }))
    }, [syncSettings]);
    

    return <div>
                <Tooltip title={
                    <ShortcutTooltipTitle label="Layout Setup">
                        <ShortcutKeys keys={['.']} />
                    </ShortcutTooltipTitle>
                } arrow placement='bottom'>
                    <ExtraSmallButton
                        id={'scalper-layout-menu-open-button'}
                        size="small"
                        ref={buttonRef}
                        color="inherit"
                        onClick={handleOpenPopover}
                        // disabled={menuDisabled}
                        data-event={'Scalper Layout Button Clicked'}
                        data-event-source={'stockbar'}
                    >
                        <MultiPaneMenuIcon />
                    </ExtraSmallButton>
                </Tooltip>
                {open && <AppMenu
                            open={open}
                            anchorEl={anchorEl}
                            clickAwayIgnoreRefs={[buttonRef]}
                            container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}
                            onClose={handleClosePopover}
                            slotProps={{
                                paper: {
                                    id: 'scalper-layout-menu'
                                }
                            }}
                        >
                            <Box id={'multipane-menu-layout'} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <>{menuItems}</>
                                <Divider sx={{ margin: theme.spacing(1,0) }} />
                                <Typography variant={'caption'} color="textSecondary" sx={{ p: '6px 16px' }}>SYNC IN LAYOUT</Typography>
                                {syncSettings && syncSettingsMenuItems.map(({key, label}) => {
                                    if (!syncSettingsKeys.includes(key)) return null;
                                    return <MenuItem
                                        key={key}
                                        sx={{
                                            padding: 0
                                        }}
                                    >
                                        <FormControlLabel
                                            value={key}
                                            onChange={() => handleSwitchChange(key)}
                                            disableTypography
                                            control={
                                                <ToggleSwitch
                                                    size={'small'}
                                                    checked={syncSettings[key]}
                                                    disableRipple
                                                    data-event-source={'multipane'}
                                                    data-event-toggle={`${syncSettings[key] ? 'OFF' : 'ON'}`}
                                                    data-event={`${label} Sync`}
                                                />
                                            }
                                            sx={{
                                                padding: '4px 16px',
                                                 margin: 0,
                                                 width: '100%'
                                                }}
                                            labelPlacement={'start'}
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: '100%',
                                                        opacity: 0.9
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            }
                                        />
                                    </MenuItem>
                                })}
                            </Box>
                        </AppMenu>}
            </div>;
};

export default memo(ScalperLayoutMenu);